// Export limits, shared by the workbook and the app's export screen. Keep this
// module free of exceljs: the app imports it eagerly through the
// "./export/limits" package export, while the workbook is lazy-loaded.

/** Most sightings one export includes; more matches are cut off at the cap. */
export const EXPORT_MAX_ROWS = 10_000;

/** Longest applied date range an export may cover. */
export const EXPORT_MAX_RANGE_DAYS = 7;

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Allowed beyond `EXPORT_MAX_RANGE_DAYS` days: an hour for a window that
 * crosses a daylight-saving change, plus a minute because the date presets
 * read the clock separately for the start and the end (`subDays(new Date(), 7)`
 * then `new Date()`), so their span is always a few milliseconds over.
 */
const RANGE_ALLOWANCE_MS = HOUR_MS + MINUTE_MS;

/** The applied filter range: ISO instants, empty when unset. */
export interface ExportRange {
  startDate: string;
  endDate: string;
}

/**
 * Whether the applied date range may be exported: at most
 * `EXPORT_MAX_RANGE_DAYS` days, plus `RANGE_ALLOWANCE_MS` so a "Last 7 days"
 * window isn't rejected when it crosses a daylight-saving change.
 */
export function isExportRangeAllowed({ startDate, endDate }: ExportRange): boolean {
  if (!startDate || !endDate) return false;
  const span = Date.parse(endDate) - Date.parse(startDate);
  // NaN (an unparseable date) fails both comparisons, so it is rejected too.
  return span >= 0 && span <= EXPORT_MAX_RANGE_DAYS * DAY_MS + RANGE_ALLOWANCE_MS;
}
